import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";

const NAV = [
  { label: "About", href: "/#about" },
  { label: "Collection", href: "/#collection" },
  { label: "Doors", href: "https://atelierdoors.com", external: true },
  { label: "Commercial", href: "/commercial" },
  { label: "Aluminum", href: "/#aluminum" },
  { label: "Gallery", href: "/#gallery" },
  { label: "Journal", href: "/blog" },
];

export default function Header({ onRequestQuote, onDownloadCatalog, solid = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 md:top-8 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || solid
          ? "bg-[#0a1a17]/90 backdrop-blur-xl border-b border-[#c99658]/15"
          : "bg-gradient-to-b from-[#0a1a17]/70 via-[#0a1a17]/30 to-transparent"
      }`}
    >
      <div className="container-vlx flex items-center justify-between gap-4 h-20 md:h-24">
        <a
          href="/"
          data-testid="header-logo"
          aria-label="ViewLux Colorado — Home"
          className="flex items-center gap-3 group flex-shrink-0"
        >
          <img
            src="/viewlux/logo.png"
            alt="ViewLux Windows and Doors"
            className="h-12 md:h-14 w-auto transition-transform duration-500 group-hover:scale-[1.03]"
          />
          <span className="hidden 2xl:inline-flex items-center gap-2 ml-1 pl-3 border-l border-[#c99658]/30 text-[#e2b880] text-[10px] tracking-[0.45em] uppercase font-raleway whitespace-nowrap">
            Colorado
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-0 xl:gap-0.5 2xl:gap-1">
          {NAV.map((n) => (
            <a
              key={n.label}
              href={n.href}
              target={n.external ? "_blank" : undefined}
              rel={n.external ? "noopener noreferrer" : undefined}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="relative px-2.5 xl:px-3 2xl:px-4 py-2 text-[10px] xl:text-[10.5px] 2xl:text-[11px] font-medium tracking-[0.2em] xl:tracking-[0.24em] 2xl:tracking-[0.3em] text-white/80 font-raleway uppercase transition-colors duration-300 hover:text-[#e2b880] whitespace-nowrap"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 2xl:gap-4">
          <a
            href="tel:7207703795"
            data-testid="header-phone"
            className="hidden 2xl:inline-flex items-center gap-2 min-h-[44px] py-2 text-white/90 text-[12px] font-medium tracking-[0.18em] hover:text-[#e2b880] transition-colors font-raleway"
          >
            <Phone className="w-3.5 h-3.5" />
            720.770.3795
          </a>
          <button
            data-testid="header-cta-catalog"
            onClick={onDownloadCatalog}
            className="hidden lg:inline-flex items-center gap-2 px-2 py-2 text-[10px] xl:text-[10.5px] 2xl:text-[11px] font-medium tracking-[0.2em] xl:tracking-[0.24em] 2xl:tracking-[0.3em] uppercase font-raleway text-[#e2b880] hover:text-white transition-colors whitespace-nowrap"
          >
            Catalog
          </button>
          <button
            data-testid="header-cta-quote"
            onClick={onRequestQuote}
            className="hidden md:inline-flex btn-pill btn-primary !py-2.5 !px-3.5 xl:!py-2.5 xl:!px-4 2xl:!py-3 2xl:!px-6 !text-[10px] xl:!text-[10.5px] 2xl:!text-[11px] whitespace-nowrap"
          >
            Request Quote
          </button>
          <button
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 min-w-[44px] min-h-[44px] border border-[#c99658]/30 text-[#e2b880]"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#0a1a17]/97 backdrop-blur-xl border-t border-[#c99658]/15"
        >
          <div className="container-vlx py-6 flex flex-col gap-3">
            {NAV.map((n) => (
              <a
                key={n.label}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                href={n.href}
                target={n.external ? "_blank" : undefined}
                rel={n.external ? "noopener noreferrer" : undefined}
                onClick={() => setOpen(false)}
                className="text-white/90 font-raleway uppercase tracking-[0.32em] text-xs py-3 border-b border-[#c99658]/15"
              >
                {n.label}
              </a>
            ))}
            <button
              data-testid="mobile-nav-catalog"
              onClick={() => {
                setOpen(false);
                onDownloadCatalog?.();
              }}
              className="text-[#e2b880] font-raleway uppercase tracking-[0.32em] text-xs py-3 border-b border-[#c99658]/15 text-left"
            >
              Download Catalog
            </button>
            <button
              data-testid="mobile-nav-quote"
              onClick={() => {
                setOpen(false);
                onRequestQuote?.();
              }}
              className="btn-pill btn-primary self-start mt-3"
            >
              Request Quote
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
